/**
 * @copyright Copyright 2024=2025 Epic Systems Corporation
 * @file A React Select wrapper that progressively loads options as the user scrolls
 * @module Epic.AppOrchard.ConnectorManagement.Shared.ProgressiveLoadComboBox
 */

import { useToken } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	default as ReactSelect,
	GroupBase,
	InputActionMeta,
	OptionsOrGroups,
	StylesConfig,
} from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";

export interface IProgressiveLoadComboBoxProps<
	Option,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<StateManagerProps<Option, IsMulti, Group>, "filterOption"> {
	numberToLoad?: number;
	filterFunction?: (option: Option, rawInput: string) => boolean;
}

export const ProgressiveLoadComboBox = <
	Option,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>
>(
	props: IProgressiveLoadComboBoxProps<Option, IsMulti, Group>,
) => {
	const {
		options = [],
		numberToLoad = 25,
		onInputChange,
		onMenuScrollToBottom,
		filterFunction,
		...rest
	} = props;
	const [isLoading, setIsLoading] = useState(false);
	const isFiltering = useRef(false);
	const [visibleOptions, setVisibleOptions] = useState(
		options.slice(0, numberToLoad) as OptionsOrGroups<Option, Group>,
	);
	const [prevVisibleOptions, setPrevVisibleOptions] = useState(visibleOptions);
	const [filteredOptions, setFilteredOptions] = useState(options);

	const handleScroll = (event: WheelEvent | TouchEvent) => {
		if (!isLoading) {
			setIsLoading(true);
			setVisibleOptions(filteredOptions.slice(0, visibleOptions.length + numberToLoad));
		}
		onMenuScrollToBottom && onMenuScrollToBottom(event);
	};

	const handleInputChange = (inputValue: string, actionMeta: InputActionMeta) => {
		if (actionMeta.action === "input-change") {
			setIsLoading(true);
			if (!isFiltering.current) {
				setPrevVisibleOptions(visibleOptions);
				isFiltering.current = true;
			}
			const newOptions = options.filter((option) =>
				filterFunction
					? filterFunction(option as Option, inputValue)
					: JSON.stringify(option ?? "")
							.toLowerCase()
							.includes(inputValue.toLowerCase()),
			);

			setFilteredOptions(newOptions);
			setVisibleOptions(newOptions.slice(0, numberToLoad));
		}
		if (
			(actionMeta.action === "set-value" || actionMeta.action === "menu-close") &&
			isFiltering.current
		) {
			setVisibleOptions(prevVisibleOptions);
			setFilteredOptions(options);
			isFiltering.current = false;
		}
		onInputChange && onInputChange(inputValue, actionMeta);
	};

	useEffect(() => {
		if (isLoading) {
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleOptions]);

	useEffect(() => {
		setVisibleOptions(options.slice(0, numberToLoad));
	}, [numberToLoad, options]);

	const errorRed = useToken("colors", "red.500");

	const styles: StylesConfig<Option, IsMulti, Group> = useMemo(() => {
		return {
			...rest.styles,
			control: (base, state) => {
				return {
					...base,
					...(rest["aria-invalid"] &&
						!state.isFocused && {
							"border-color": errorRed,
							"box-shadow": `${errorRed} 0px 0px 0px 1px`,
						}),
				};
			},
		};
	}, [errorRed, rest]);

	return (
		<ReactSelect
			options={visibleOptions}
			onMenuScrollToBottom={handleScroll}
			onInputChange={handleInputChange}
			isLoading={isLoading}
			styles={styles}
			{...rest}
		/>
	);
};
